/**
 * RTK Query API for Feedback operations
 *
 * This API replaces the deprecated `redux/reducers/feedbacks/feedbacksSlice`
 * and `redux/reducers/feedbacks/feedbackSlice`.
 * All feedback operations should use these hooks instead of Redux actions.
 *
 * @example
 * // Fetching feedbacks
 * const { data, isLoading } = useGetFeedbacksQuery({ page: 1, limit: 20 });
 *
 * @example
 * // Getting a single feedback
 * const { data: feedback } = useGetFeedbackQuery({ id: feedbackId });
 *
 * @example
 * // Creating a feedback
 * const [createFeedback, { isLoading }] = useCreateFeedbackMutation();
 * await createFeedback({ title: 'Bug Report', description: '...' });
 *
 * @example
 * // Updating a feedback
 * const [updateFeedback] = useUpdateFeedbackMutation();
 * await updateFeedback({ id: feedbackId, data: { status: 'resolved' } });
 */
import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";

const feedbacksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get paginated list of feedbacks
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (1-based)
     * @param {number} params.limit - Items per page
     * @param {string} params.parent - Parent company ID
     * @returns Paginated feedback list
     */
    getFeedbacks: builder.query({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = getSearchParams({
          page,
          limit,
          ...rest,
        });
        return `feedback?${query}`;
      },
      transformResponse: (response) => response,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({
                type: "Feedbacks",
                id: _id,
              })),
              { type: "Feedbacks", id: "LIST" },
            ]
          : [{ type: "Feedbacks", id: "LIST" }],
    }),

    /**
     * Get a single feedback by ID
     * @param {Object} params - Query parameters
     * @param {string} params.id - Feedback ID
     * @returns Feedback object
     */
    getFeedback: builder.query({
      query: ({ id, ...rest }) => `feedback/${id}?${getSearchParams(rest)}`,
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, { id }) => [{ type: "Feedbacks", id }],
    }),

    /**
     * Create a new feedback
     * @param {Object} data - Feedback data
     * @param {string} data.title - Feedback title
     * @param {string} data.description - Feedback description
     * @param {string} data.type - Feedback type (bug, feature, etc.)
     * @returns Created feedback object
     */
    createFeedback: builder.mutation({
      query: (data) => ({
        url: "feedback",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Feedbacks", id: "LIST" }],
    }),

    /**
     * Update an existing feedback
     * @param {Object} params - Update parameters
     * @param {string} params.id - Feedback ID
     * @param {Object} params.data - Updated feedback data
     * @returns Updated feedback object
     */
    updateFeedback: builder.mutation({
      query: ({ id, data, ...rest }) => ({
        url: `feedback/${id}?${getSearchParams(rest)}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Feedbacks", id },
        { type: "Feedbacks", id: "LIST" },
      ],
    }),

    /**
     * Delete one or more feedbacks
     * @param {Object} params - Delete parameters
     * @param {string[]} params.ids - Array of feedback IDs to delete
     * @param {string} params.parent - Parent company ID
     * @returns Deletion result
     */
    deleteFeedbacks: builder.mutation({
      query: (payload) => {
        const isArrayPayload = Array.isArray(payload);
        const { ids, ...rest } = !isArrayPayload && payload ? payload : {};
        const body = isArrayPayload
          ? payload
          : Array.isArray(ids)
          ? ids
          : payload?.data || [];

        return {
          url: `feedback?${getSearchParams(rest)}`,
          method: "DELETE",
          body,
        };
      },
      invalidatesTags: [{ type: "Feedbacks", id: "LIST" }],
    }),

    /**
     * Add a message to a feedback thread
     * @param {Object} params - Message parameters
     * @param {string} params.feedbackId - Feedback ID
     * @param {Object} params.data - Message data
     * @returns Updated feedback with new message
     */
    addFeedbackMessage: builder.mutation({
      query: ({ feedbackId, data, ...rest }) => ({
        url: `feedback/${feedbackId}/message?${getSearchParams(rest)}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { feedbackId }) => [
        { type: "Feedbacks", id: feedbackId },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFeedbacksQuery,
  useGetFeedbackQuery,
  useLazyGetFeedbackQuery,
  useCreateFeedbackMutation,
  useUpdateFeedbackMutation,
  useDeleteFeedbacksMutation,
  useAddFeedbackMessageMutation,
} = feedbacksApi;



